import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Divider,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import {
    Payment as PaymentIcon,
    AccountBalance as BankIcon,
    CreditCard as CardIcon,
    Receipt as ReceiptIcon,
    Security as SecurityIcon,
    CheckCircle as CheckIcon,
    Warning as WarningIcon,
    Visibility as ViewIcon,
    ArrowBack as BackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import laboratoryService from '../../services/testinglaboratoryService';
import feesService from '../../services/TestingFeesService';
import paymentService from '../../services/TestingPaymentService';



const PaymentPage = () => {
    const navigate = useNavigate();

    // Get payment details from location state or localStorage
    const [paymentDetails, setPaymentDetails] = useState();
    const [activeStep, setActiveStep] = useState(0);
    const [paymentStatus, setPaymentStatus] = useState('pending');
    const [loading, setLoading] = useState(false);
    const [deductTDS, setDeductTDS] = useState(false);
    const [tanNumber, setTanNumber] = useState('');
    const [labBasicDetails, setLabBasicDetails] = useState(null);
    const [labDetailsLoading, setLabDetailsLoading] = useState(true);
    const [labDetailsError, setLabDetailsError] = useState(null);
    const [feesLoading, setFeesLoading] = useState(true);
    const [feesError, setFeesError] = useState(null);

    useEffect(() => {
        // Fetch both basic details and fees data from API
        fetchBasicDetails();
        fetchFeesData();

        // Check if payment was recently completed
        checkRecentPayment();

        // Check URL params for payment status (in case user came back from gateway)
        const urlParams = new URLSearchParams(window.location.search);
        const paymentSuccess = urlParams.get('payment_success');
        const orderId = urlParams.get('order_id');

        if (paymentSuccess === 'true' && orderId) {
            setPaymentStatus('success');
            setActiveStep(2);
            console.log('Payment success detected from URL params');
        } else if (paymentSuccess === 'false') {
            setPaymentStatus('failed');
            console.log('Payment failure detected from URL params');
        }
    }, [navigate]);

    const checkRecentPayment = async () => {
        try {
            const paymentCompletion = await paymentService.checkPaymentCompletion();
            if (paymentCompletion.isCompleted) {
                setPaymentStatus('success');
                setActiveStep(2);
                console.log('Recent successful payment found:', paymentCompletion.paymentDetails);
            }
        } catch (error) {
            console.error('Error checking recent payment:', error);
        }
    };

    const fetchFeesData = async () => {
        try {
            setFeesLoading(true);
            setFeesError(null);

            // Get AppId from localStorage
            const appId = localStorage.getItem('appId') || '';

            const response = await feesService.getFeesByAppId(appId);
            console.log('Raw API Response:', response);

            if (response.status === 'success' && response.data) {
                // Process fees data from your API response format
                const feesBreakdown = response.data.feesBreakdown || [];

                // Convert string fees to numbers and ensure proper structure
                const processedFeesBreakdown = feesBreakdown.map(item => ({
                    ...item,
                    fees: parseInt(item.fees) || 0 // Convert string to number
                }));

                // Use the summary from API response (already calculated)
                const summary = response.data.summary || {
                    grossTotal: 0,
                    gstAmount: 0,
                    gstRate: 18,
                    grandTotal: 0
                };

                setPaymentDetails({
                    appId: response.data.appId || appId,
                    feesBreakdown: processedFeesBreakdown,
                    summary: summary,
                    cabType: response.data.cabType,
                    hasMedicalImaging: response.data.hasMedicalImaging
                });

                console.log('Fees data loaded from API:', response.data);
            } else {
                throw new Error(response.message || 'Failed to fetch fees data');
            }
        } catch (error) {
            console.error('Error fetching fees data:', error);
            setFeesError(error.message);
        } finally {
            setFeesLoading(false);
        }
    };

    const fetchBasicDetails = async () => {
        try {
            setLabDetailsLoading(true);
            setLabDetailsError(null);

            // Get AppId from localStorage, default to ''
            const appId = localStorage.getItem('appId') || '';

            const response = await laboratoryService.getPaymentBasicDetail(appId);

            if (response.success && response.data) {
                setLabBasicDetails({
                    LaboratoryName: response.data.CabName || 'N/A',
                    ContactEmail: response.data.ContactEmail || response.data.Email || 'N/A',
                    ContactMobile: response.data.ContactPersonMobileNo || response.data.ContactPersonMobileNo || 'N/A',
                    OrgId: response.data.OrgId || 'N/A',
                    AppId: response.data.AppId || appId,
                    Address: response.data.Address || response.data.AddressLine1 || 'N/A',
                    City: response.data.City || 'N/A',
                    State: response.data.State || 'N/A',
                    LaboratoryType: response.data.LaboratoryType || 'Testing Laboratory',
                    GSTNumber: response.data.GSTNumber || '',
                    ContactPerson: response.data.ContactPerson || '',
                    TAN: response.data.TAN || '',
                    Pincode: response.data.Pincode || response.data.Pin || ''
                });
                console.log('Basic details loaded from API:', response.data);
            } else {
                throw new Error(response.message || 'Failed to fetch basic details');
            }
        } catch (error) {
            console.error('Error fetching basic details:', error);
            setLabDetailsError(error.message);

            // Get AppId for fallback data
            const appId = localStorage.getItem('appId') || '';

            // Fallback to mock data if API fails
            setLabBasicDetails({
                LaboratoryName: 'Sample Laboratory',
                ContactEmail: 'sample@lab.com',
                ContactMobile: '9999999999',
                OrgId: '12345',
                AppId: appId,
                Address: 'Sample Address',
                City: 'New Delhi',
                State: 'Delhi',
                LaboratoryType: 'Medical Laboratory'
            });
        } finally {
            setLabDetailsLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '₹0.00';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    const calculateTDSAmount = () => {
        if (!deductTDS || !paymentDetails?.summary) return 0;
        // Simple TDS calculation: 2% of gross total
        return (paymentDetails.summary.grossTotal * 2) / 100;
    };

    const getFinalAmount = () => {
        if (!paymentDetails?.summary) return 0;
        const totalAmount = paymentDetails.summary.grandTotal;
        const tdsAmount = calculateTDSAmount();
        return totalAmount - tdsAmount;
    };

    const processPayment = async () => {
        setLoading(true);
        try {
            // Get current user session
            const userSession = authService.getUserData();
            if (!userSession) {
                throw new Error('User session not found. Please login again.');
            }

            // Validate TDS requirements
            if (deductTDS && !tanNumber.trim()) {
                throw new Error('TAN number is required when TDS is selected');
            }

            // Show processing status
            setPaymentStatus('processing');

            // Prepare payment data for ICICI gateway
            const finalAmount = deductTDS ? getFinalAmount() : paymentDetails?.summary?.grandTotal || 0;
            const paymentData = {
                CustomerID: userSession.orgId || labBasicDetails?.OrgId,
                TxnAmount: finalAmount,
                BankReferenceNo: `NABL${Date.now()}`,
                Description: 'NABL Accreditation Application Fee',
                email: labBasicDetails?.ContactEmail || userSession.email,
                phone: labBasicDetails?.ContactMobile || userSession.mobile,
                laboratoryName: labBasicDetails?.LaboratoryName,
                applicationNo: labBasicDetails?.AppId || paymentDetails?.appId,
                orgId: userSession.orgId || labBasicDetails?.OrgId,
                appId: labBasicDetails?.AppId || paymentDetails?.appId,
                paymentType: '2', // Application fee
                tdsDeducted: deductTDS,
                tdsAmount: deductTDS ? calculateTDSAmount() : 0,
                tan: tanNumber,
                userName: localStorage.getItem('username') || userSession.name
            };

            console.log('Processing ICICI payment with data:', paymentData);

            // Call our ICICI payment API using the payment service
            const apiPaymentData = {
                appId: labBasicDetails?.AppId || localStorage.getItem('appId') || '',
                orgId: userSession.orgId || labBasicDetails?.OrgId,
                paymentTypeId: '2', // Application fee
                amount: paymentDetails?.summary?.grossTotal || 0,
                gstAmount: paymentDetails?.summary?.gstAmount || 0,
                totalAmount: finalAmount,
                currency: 'INR',
                email: labBasicDetails?.ContactEmail || userSession.email,
                mobile: labBasicDetails?.ContactMobile || userSession.mobile,
                labTypeId: '1', // Medical laboratory
                chkDeductTds: deductTDS,
                TDSAmount: deductTDS ? calculateTDSAmount() : 0,
                TDSPercent: deductTDS ? 2 : 0,
                tan: tanNumber,
                userName: localStorage.getItem('username') || userSession.name
            };

            console.log('API Payment Data:', apiPaymentData);

            // Use the payment service to initiate ICICI payment
            const result = await paymentService.initiateICICIPayment(apiPaymentData);

            if (result.success && result.data && result.data.paymentUrl) {
                console.log('ICICI EasyPay URL generated, redirecting...');

                // Store payment information for tracking
                localStorage.setItem('currentOrderId', result.data.orderId);
                localStorage.setItem('paymentInitiated', JSON.stringify({
                    orderId: result.data.orderId,
                    amount: result.data.amount,
                    appId: apiPaymentData.appId,
                    orgId: apiPaymentData.orgId,
                    timestamp: new Date().toISOString()
                }));

                // Show success message before redirect
                setPaymentStatus('redirecting');

                // Small delay to show the status, then redirect
                setTimeout(() => {
                    window.location.href = result.data.paymentUrl;
                }, 1000);

                return;
            } else {
                throw new Error(result.message || 'Failed to generate payment URL from server');
            }

        } catch (error) {
            console.error('Payment processing error:', error);
            setPaymentStatus('failed');

            // Show error message
            let errorMessage = `Payment processing failed: ${error.message}`;

            if (error.message.includes('session')) {
                errorMessage += '\n\nPlease login again and retry.';
            } else if (error.message.includes('TAN')) {
                errorMessage += '\n\nPlease provide a valid TAN number for TDS deduction.';
            } else if (error.message.includes('gateway')) {
                errorMessage += '\n\nPlease check your internet connection and try again.';
            }

            alert(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const steps = [
        'Review Payment Details',
        'Verify & Pay',
        'Payment Confirmation'
    ];
     const handlePerformaInvoice = () => {
        const orgId = localStorage.getItem('orgId');
        const appId = localStorage.getItem('appId');

        
        const previewUrl = `http://nablwpnew.qci.org.in/InvoiceApplicationPerfomaQR?appId=${appId}&payType=2`;
        
        // Open in new tab/window
        window.open(previewUrl, '_blank');
        
    };

    // Render lab basic details section
    const renderLabDetails = () => (
        <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Laboratory Information
                </Typography>

                {labDetailsLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Loading laboratory details...
                        </Typography>
                    </Box>
                ) : labDetailsError ? (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        <Typography variant="body2">
                            Could not load laboratory details: {labDetailsError}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Using fallback data for payment processing.
                        </Typography>
                    </Alert>
                ) : labBasicDetails ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                                Laboratory Name
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                                {labBasicDetails.LaboratoryName}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                                Organization ID
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                                {labBasicDetails.OrgId}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                                Contact Email
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                                {labBasicDetails.ContactEmail || labBasicDetails.ContactPersonEmail2 || 'Not provided'}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                                Contact Mobile
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                                {labBasicDetails.ContactMobile || 'Not provided'}
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                                Address
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                                {labBasicDetails.Address ||
                                    [labBasicDetails.AddressLine1, labBasicDetails.AddressLine2].filter(Boolean).join(', ') ||
                                    'Address not provided'}
                                {(labBasicDetails.Pincode || labBasicDetails.Pin) && ` - ${labBasicDetails.Pincode || labBasicDetails.Pin}`}
                            </Typography>
                        </Grid>

                        {(labBasicDetails.State || labBasicDetails.City) && (
                            <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                    Location
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                                    {[labBasicDetails.City, labBasicDetails.State].filter(Boolean).join(', ')}
                                </Typography>
                            </Grid>
                        )}

                        {labBasicDetails.LaboratoryType && (
                            <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                    Laboratory Type
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                                    {labBasicDetails.LaboratoryType}
                                </Typography>
                            </Grid>
                        )}

                        {labBasicDetails.GSTNumber && (
                            <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                    GST Number
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                                    {labBasicDetails.GSTNumber}
                                </Typography>
                            </Grid>
                        )}

                        {labBasicDetails.ContactPerson && (
                            <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                    Contact Person
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                                    {labBasicDetails.ContactPerson}
                                </Typography>
                            </Grid>
                        )}

                        {labBasicDetails.TAN && (
                            <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                    TAN Number
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                                    {labBasicDetails.TAN}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                ) : (
                    <Alert severity="info" sx={{ mt: 1 }}>
                        Using sample laboratory details for demonstration.
                    </Alert>
                )}
            </CardContent>
        </Card>
    );

    const renderPaymentSummary = () => (
        <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Payment Summary
                    {paymentDetails?.cabType && (
                        <Chip
                            label={paymentDetails.cabType}
                            size="small"
                            sx={{ ml: 2 }}
                            color="info"
                        />
                    )}
                </Typography>

                {feesLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Loading fees information...
                        </Typography>
                    </Box>
                ) : feesError ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            Could not load fees data: {feesError}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Using fallback data for payment processing.
                        </Typography>
                    </Alert>
                ) : null}

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Description</TableCell>
                                <TableCell>Fee Type</TableCell>
                                <TableCell align="right">Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paymentDetails?.feesBreakdown?.map((item, index) => (
                                <TableRow key={index}>
                                    <TableCell>{item.category}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={item.FeeType || 'Standard'}
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">{formatCurrency(item.fees)}</TableCell>
                                </TableRow>
                            ))}

                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Gross Total</TableCell>
                                <TableCell></TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                    {formatCurrency(paymentDetails?.summary?.grossTotal)}
                                </TableCell>
                            </TableRow>

                            <TableRow>
                                <TableCell>GST ({paymentDetails?.summary?.gstRate || 18}%)</TableCell>
                                <TableCell></TableCell>
                                <TableCell align="right" sx={{ color: 'error.main' }}>
                                    {formatCurrency(paymentDetails?.summary?.gstAmount)}
                                </TableCell>
                            </TableRow>

                            {deductTDS && (
                                <TableRow>
                                    <TableCell>TDS (2%)</TableCell>
                                    <TableCell></TableCell>
                                    <TableCell align="right" sx={{ color: 'warning.main' }}>
                                        -{formatCurrency(calculateTDSAmount())}
                                    </TableCell>
                                </TableRow>
                            )}

                            <TableRow sx={{ backgroundColor: 'primary.light' }}>
                                <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>
                                    {deductTDS ? 'Final Amount (After TDS)' : 'Grand Total'}
                                </TableCell>
                                <TableCell></TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1em', color: 'white' }}>
                                    {formatCurrency(deductTDS ? getFinalAmount() : paymentDetails?.summary?.grandTotal)}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );

    const renderPaymentForm = () => (
        <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    <PaymentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    ICICI Payment Gateway
                </Typography>

                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        ICICI EasyPay Production Gateway
                    </Typography>
                    <Typography variant="body2">
                        You will be redirected to the secure ICICI payment gateway to complete your transaction.
                        We support multiple payment methods including Credit/Debit Cards, Net Banking, and UPI.
                    </Typography>
                </Alert>

                {paymentStatus === 'processing' && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            Connecting to ICICI Payment Gateway...
                        </Typography>
                        <Typography variant="body2">
                            Please wait while we process your payment request and prepare the secure gateway connection.
                        </Typography>
                    </Alert>
                )}

                {paymentStatus === 'redirecting' && (
                    <Alert severity="success" sx={{ mb: 3 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            Redirecting to ICICI EasyPay Gateway...
                        </Typography>
                        <Typography variant="body2">
                            Payment URL generated successfully. You will be redirected to the secure payment gateway in a moment.
                            Do not close this window or press the back button.
                        </Typography>
                    </Alert>
                )}

                {/* TDS Deduction Option */}
                <Box sx={{ p: 2, backgroundColor: 'warning.50', borderRadius: 1 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={deductTDS}
                                onChange={(e) => setDeductTDS(e.target.checked)}
                                color="warning"
                            />
                        }
                        label="Deduct TDS (2%)"
                    />

                    {deductTDS && (
                        <Box sx={{ mt: 2 }}>
                            <TextField
                                fullWidth
                                label="TAN Number"
                                value={tanNumber}
                                onChange={(e) => setTanNumber(e.target.value)}
                                variant="outlined"
                                size="small"
                                required
                                helperText="Tax Deduction and Collection Account Number is required for TDS"
                            />
                            <Alert severity="info" sx={{ mt: 1 }}>
                                TDS Amount: {formatCurrency(calculateTDSAmount())}<br />
                                Final Amount: {formatCurrency(getFinalAmount())}
                            </Alert>
                        </Box>
                    )}
                </Box>

                {/* Payment Summary */}
                <Box sx={{ mt: 3, p: 2, backgroundColor: 'primary.50', borderRadius: 1 }}>
                    <Typography variant="h6" gutterBottom>Final Payment Amount</Typography>
                    <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                        {formatCurrency(deductTDS ? getFinalAmount() : paymentDetails?.summary?.grandTotal)}
                    </Typography>
                    {deductTDS && (
                        <Typography variant="body2" color="text.secondary">
                            (After TDS deduction of {formatCurrency(calculateTDSAmount())})
                        </Typography>
                    )}
                </Box>
            </CardContent>
        </Card>
    );

    const renderPaymentConfirmation = () => (
        <Card elevation={2} sx={{ mb: 3, textAlign: 'center' }}>
            <CardContent>
                {paymentStatus === 'success' ? (
                    <>
                        <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                        <Typography variant="h5" gutterBottom sx={{ color: 'success.main', fontWeight: 'bold' }}>
                            Payment Successful!
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Your payment of {formatCurrency(paymentDetails?.summary?.grandTotal)} has been processed successfully.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Transaction ID: TXN{Date.now()}
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/main/testing/declaration')}
                            sx={{ mr: 2 }}
                        >
                            Continue to Application
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => window.print()}
                        >
                            Print Receipt
                        </Button>
                    </>
                ) : (
                    <>
                        <WarningIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                        <Typography variant="h5" gutterBottom sx={{ color: 'error.main', fontWeight: 'bold' }}>
                            Payment Failed
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            There was an issue processing your payment. Please try again.
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => {
                                setActiveStep(1);
                                setPaymentStatus('pending');
                            }}
                            color="error"
                        >
                            Retry Payment
                        </Button>
                    </>
                )}
            </CardContent>
        </Card>
    );

    if (feesLoading || labDetailsLoading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>Loading Payment Information...</Typography>
                <Typography variant="body2" color="text.secondary">
                    Please wait while we fetch your fees and laboratory details.
                </Typography>
            </Box>
        );
    }

    if (!paymentDetails) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Alert severity="error">
                    <Typography variant="h6" gutterBottom>Payment Details Not Found</Typography>
                    <Typography>Please return to the fees page to proceed with payment.</Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/main/testing/fees')}
                        sx={{ mt: 2 }}
                    >
                        Go to Fees Page
                    </Button>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, mt: 5, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
            {/* Header */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Button
                        startIcon={<BackIcon />}
                        onClick={() => navigate('/main/testing/declaration')}
                        sx={{ mr: 2 }}
                    >
                        Back
                    </Button>
                    <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                        Payment Gateway
                    </Typography>
                </Box>

                <Typography variant="subtitle1" color="text.secondary">
                    Complete your payment for NABL Accreditation Application
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, gap: 1 }}>
                    <SecurityIcon sx={{ color: 'success.main' }} />
                    <Typography variant="body2" color="success.main">
                        Secure Payment Gateway
                    </Typography>
                </Box>
            </Paper>

            {/* Stepper */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Stepper activeStep={activeStep} orientation="horizontal">
                    {steps.map((label, index) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Paper>

            <Grid container spacing={3}>
                <Grid item xs={12} lg={8}>
                    {/* Payment Content */}
                    {activeStep === 0 && (
                        <>
                            {renderLabDetails()}
                            {renderPaymentSummary()}
                            <Box sx={{ textAlign: 'center' }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    sx={{ mr: 2 }}
                                    onClick={() => setActiveStep(1)}
                                >
                                    Proceed to Payment
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<ViewIcon />}
                                    onClick={handlePerformaInvoice}
                                    disabled={loading}
                                    size="large"
                                    color="secondary"
                                    sx={{
                                        borderColor: 'secondary.main',
                                        '&:hover': {
                                            borderColor: 'secondary.dark',
                                            backgroundColor: 'secondary.50'
                                        }
                                    }}
                                >
                                    Performa Invoice
                                </Button>
                            </Box>
                        </>
                    )}

                    {activeStep === 1 && (
                        <>
                            {renderPaymentForm()}
                            <Box sx={{ textAlign: 'center', display: 'flex', gap: 2, justifyContent: 'center' }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => setActiveStep(0)}
                                    disabled={loading}
                                >
                                    Back
                                </Button>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={processPayment}
                                    disabled={loading || paymentStatus === 'processing' || paymentStatus === 'redirecting'}
                                    startIcon={loading || paymentStatus === 'processing' || paymentStatus === 'redirecting' ? null : <PaymentIcon />}
                                    sx={{
                                        minWidth: '200px',
                                        backgroundColor: paymentStatus === 'processing' ? 'warning.main' :
                                            paymentStatus === 'redirecting' ? 'success.main' : 'primary.main'
                                    }}
                                >
                                    {loading ? 'Connecting to ICICI Gateway...' :
                                        paymentStatus === 'processing' ? 'Processing Payment Request...' :
                                            paymentStatus === 'redirecting' ? 'Redirecting to EasyPay...' :
                                                `Pay ${formatCurrency(deductTDS ? getFinalAmount() : paymentDetails?.summary?.grandTotal)} via ICICI`
                                    }
                                </Button>
                            </Box>
                        </>
                    )}

                    {activeStep === 2 && renderPaymentConfirmation()}
                </Grid>

                {/* Sidebar with Application Info */}
                <Grid item xs={12} lg={4}>
                    <Card elevation={2}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                                Application Details
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">Application ID</Typography>
                                <Typography variant="body1" fontWeight="bold">
                                    {paymentDetails?.appId || localStorage.getItem('appId') || ''}
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">Laboratory Type</Typography>
                                <Typography variant="body1">
                                    {'Testing Laboratory'}
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">Payment Status</Typography>
                                <Chip
                                    label={paymentStatus === 'success' ? 'Paid' : 'Pending'}
                                    color={paymentStatus === 'success' ? 'success' : 'warning'}
                                    size="small"
                                />
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Need Help?
                            </Typography>
                            <Typography variant="body2">
                                Contact NABL Support at support@nabl.gov.in or call 011-12345678
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default PaymentPage;